import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ConfirmationService} from "primeng/api";
import {WidgetData} from "../../../../../../../../core/shared/widget/widget-data";
import {WidgetFactory} from "../../../../../../../../core/shared/widget/service/widget.factory";
import {FilterCriteria} from "../../../../../../../../core/shared/filter/api/filter.criteria";
import {ObjectIdentifier} from "../../../../../../../../core/shared/basic/object-identifier";
import {ObjectIdentifierData} from "../../../../../../../../core/shared/basic/object-identifier-data";
import {MvsMessageService} from "../../../../../../../../core/shared/service/message/mvs-message.service";
import {CreditorService} from "../../../../../../service/api/creditor.service";
import {CreditorBankAccountImportService} from "../../../../../../service/api/creditor-bank-account-import.service";
import {CreditorBankAccountImportDto} from "../../../../../../model/dto/entity/creditor-bank-account-import.dto";
import {CreditorBankAccountImportStatus} from "../../../../../../enum/creditor-bank-account-import-status.enum";

/**
 * Creditor-level CAMT import review.
 *
 * Shows the creditor's bm.CreditorBankAccountImport records (filtered kreditor-scoped through the
 * creditorBankAccount → creditor relation) in a table widget, together with two actions:
 *  - "Jetzt importieren": a creditor-scoped MANUAL import trigger that runs asynchronously
 *    (POST /mvsa/bm/creditors/{id}/jobImportPendingDocuments).
 *  - "Forciert importieren": a per-row action, only available when the selected import has
 *    status DUPLICATE, that force-imports that single record synchronously
 *    (POST /mvsa/bm/creditorBankAccountImports/{id}/forceImport), guarded by a confirm dialog.
 *
 * Row selection flows in via the widget's (onObjectSelect); the selected import DTO is re-fetched
 * so its status is authoritative for enabling the force-import action.
 */
@Component({
    selector: 'bm-creditor-camt-imports',
    templateUrl: 'bm-creditor-camt-imports.component.html',
    standalone: false
})
export class BmCreditorCamtImportsComponent implements OnInit, OnChanges, OnDestroy {

    initialized: boolean;
    manualImportBusy: boolean = false;
    forceImportBusy: boolean = false;

    @Input() creditorId: number;

    camtImportsWidgetData: WidgetData;

    /** The currently selected import record (re-fetched on selection); drives the row action. */
    selectedImport: CreditorBankAccountImportDto | null = null;

    // Exposed for the template's status comparison.
    protected readonly CreditorBankAccountImportStatus = CreditorBankAccountImportStatus;

    constructor(
        protected creditorService: CreditorService,
        protected creditorBankAccountImportService: CreditorBankAccountImportService,
        protected messageService: MvsMessageService,
        protected confirmationService: ConfirmationService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.refreshComponent();
    }

    refreshComponent(): void {

        if (!this.creditorId) {
            return;
        }

        this.refreshWidget();
        this.initialized = true;
    }

    /**
     * Build the kreditor-scoped CAMT-imports table widget. Filtered over the
     * creditorBankAccount → creditor relation (creditorBankAccount.creditorId). No fixed sorting is
     * passed (an 'e.'-aliased sorting breaks the basic-QL widget); the widget uses its default order.
     */
    refreshWidget(): void {

        const filterCriteria = [
            FilterCriteria.create('creditorBankAccount.creditorId', FilterCriteria.cOperatorEqual, this.creditorId)
        ];

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Kreditor-scoped table of bm.CreditorBankAccountImport records (status incl. DUPLICATE,
         *         camtMessageId, Dokument, Datum) for CAMT import review, duplicate handling and manual import
         */
        this.camtImportsWidgetData = WidgetFactory.createWidgetTableEntityBasicQl(
            'bm.creditor.camtImports',
            'CAMT-Importe',
            'bm.CreditorBankAccountImport',
            filterCriteria,
            [],
            true
        );
    }

    /**
     * Trigger the creditor-scoped manual import of all pending CAMT documents. The import runs
     * asynchronously; on acceptance we show a hint toast and reload the table.
     */
    importPendingDocuments(): void {

        if (!this.creditorId || this.manualImportBusy) {
            return;
        }

        this.manualImportBusy = true;

        this.creditorService.importPendingDocumentsForCreditor(this.creditorId).subscribe({
            next: () => {
                this.manualImportBusy = false;
                this.messageService.showInfoMessage(
                    'CAMT-Import',
                    'Import gestartet … Die Dokumente werden im Hintergrund verarbeitet.'
                );
                this.refreshWidget();
            },
            error: (error: any) => {
                this.manualImportBusy = false;
                this.messageService.showErrorMessage(
                    'CAMT-Import',
                    error?.error?.error || error?.error?.message
                    || 'Der Import konnte nicht gestartet werden.'
                );
            }
        });
    }

    /**
     * Force-import the currently selected DUPLICATE record after a confirm dialog. Runs
     * synchronously; on success we reload the table and show a success toast.
     */
    forceImportSelected(): void {

        if (!this.canForceImportSelected() || this.forceImportBusy) {
            return;
        }

        const importId = this.selectedImport.id;

        this.confirmationService.confirm({
            header: 'Forciert importieren',
            message: 'Diesen als Duplikat markierten Import wirklich forciert einspielen?',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Ja, forcieren',
            rejectLabel: 'Abbrechen',
            accept: () => {
                this.forceImportBusy = true;
                this.creditorBankAccountImportService.forceImport(importId).subscribe({
                    next: () => {
                        this.forceImportBusy = false;
                        this.selectedImport = null;
                        this.messageService.showSuccessMessage(
                            'CAMT-Import',
                            'Der Duplikat-Import wurde forciert eingespielt.'
                        );
                        this.refreshWidget();
                    },
                    error: (error: any) => {
                        this.forceImportBusy = false;
                        this.messageService.showErrorMessage(
                            'CAMT-Import',
                            error?.error?.error || error?.error?.message
                            || 'Der Import konnte nicht forciert eingespielt werden.'
                        );
                    }
                });
            }
        });
    }

    /**
     * true when a record is selected AND it has status DUPLICATE — only then may it be force-imported.
     */
    canForceImportSelected(): boolean {
        return !!this.selectedImport && this.selectedImport.status === CreditorBankAccountImportStatus.DUPLICATE;
    }

    /**
     * Handle a row selection from the table widget: re-fetch the selected import so its status is
     * authoritative for the force-import action.
     */
    handleObjectSelect(event: ObjectIdentifier | ObjectIdentifierData): void {

        this.selectedImport = null;

        if (!event?.objectId) {
            return;
        }

        this.creditorBankAccountImportService.get(event.objectId).subscribe({
            next: (dto: CreditorBankAccountImportDto) => {
                this.selectedImport = dto;
            },
            error: () => {
                this.selectedImport = null;
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (!this.initialized) {
            return;
        }

        if (changes["creditorId"]) {
            this.selectedImport = null;
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
    }
}
